import axios from 'axios';
import React from 'react';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { formatUnixToTime } from 'src/utils/dateformatting';
import { useAppSelector } from 'src/utils/Hooks';
import { getCurrentContentVersion } from 'src/utils/app';

/** This would be changed to master-content-upload-v2 */
const MASTER_CONTENT_UPLOAD_TYPE = 'master-content-upload-v2';

const downloadTemplate = async (apiUrl: string) => {
  const response = await axios.get(apiUrl);
  if (response.data) {
    const a = document.createElement('a');
    a.href = response.data;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
      a.remove();
    }, 100);
  }
};

interface FetchLastUpdatedTimeParams {
  retailerId: number;
  targetUrl: string;
}

interface FetchLastUpdatedTimeResponse {
  isLoading: boolean;
  errorMessage?: string;
  uploadedTime?: string;
}

export const downloadCurrentMasterContent = async ({
  retailerId,
  targetUrl
}: FetchLastUpdatedTimeParams): Promise<{ errorMessage: string }> => {
  const url = `https://${targetUrl}/api/account/DownloadLatestUploadedFile`;
  const params = { retailer: retailerId, uploadType: MASTER_CONTENT_UPLOAD_TYPE };
  try {
    const res = await axios.get(url, { params });
    if (res.data) {
      const a = document.createElement('a');
      a.href = res.data;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        a.remove();
      }, 100);
    }

    return { errorMessage: '' };
  } catch (e) {
    return { errorMessage: e.response.data };
  }
};

export const fetchLastUpdatedTime = async ({
  retailerId,
  targetUrl
}: FetchLastUpdatedTimeParams): Promise<FetchLastUpdatedTimeResponse> => {
  const url = `https://${targetUrl}/api/account/GetLastUploadTime`;
  const params = { retailer: retailerId, uploadType: MASTER_CONTENT_UPLOAD_TYPE };

  try {
    const res = await axios.get(url, { params });

    if (res.status === 200) {
      return { isLoading: false, uploadedTime: formatUnixToTime(res.data.lastUploadTime, 'DD MMM YYYY hh:mm A') };
    }
  } catch (e) {
    return { isLoading: false, errorMessage: e.response.data };
  }

  return { isLoading: false };
};

/**
 * This file will have configuration for the content settings page
 * And fetching helpers to deal with API calls
 */
export const useSettingsMasterContentConfig = () => {
  const { targetUrl, queryParams } = useAppSelector((state) => state.app);
  const { searchParams } = queryParams;
  const { mainEntity } = useAppSelector((state) => state.entityService);

  const { id: currentRetailerId, availableRetailers } = useAppSelector((state) => state.retailer);
  const {
    config: { allRetailerIds }
  } = useAppSelector((state) => state.user);

  const retailersData = availableRetailers.filter((retailer) => allRetailerIds.includes(Number(retailer.id)));
  const retailersOption = retailersData.map((datum) => {
    return { id: datum.id, name: datum.name, label: datum.displayName };
  });
  const defaultRetailer =
    retailersOption.find((retailer) => retailer.id === String(currentRetailerId)) || retailersOption[0];

  if (!mainEntity || !retailersOption) {
    return { isInitialLoading: true };
  }

  const { hashId } = mainEntity;

  // Determine the correct tab and subtab based on content version
  const currentContentVersion = getCurrentContentVersion();
  let contentTab = 'content';
  let contentSubtab = 'contentAccuracy';
  
  if (currentContentVersion === 'contentV2') {
    contentTab = 'contentV2';
    contentSubtab = 'contentAccuracyV2';
  } else if (currentContentVersion === 'contentV3') {
    contentTab = 'contentV3';
    contentSubtab = 'contentAccuracy';
  }

  return {
    description: (
      <Text variant="body2">
        Upload content for products in your catalog. After successfully uploading, you can visit the{' '}
        <a
          href={`/client/${hashId}${searchParams}&tab=${contentTab}&subtab=${contentSubtab}`}
          style={{ fontWeight: 500, fontSize: '14px', textDecoration: 'underline' }}
        >
          Content Accuracy page
        </a>{' '}
        to measure how this compares to the live content on the retailer&apos;s website.
      </Text>
    ),
    retailersOption,
    defaultRetailer,
    defaultStatusUploadName: 'master content',
    downloadBlankTemplate: () =>
      downloadTemplate(
        `https://${targetUrl}/api/account/DownloadUploadTemplate?templateType=${MASTER_CONTENT_UPLOAD_TYPE}`
      ),
    downloadCurrentMasterContent: ({ retailerId }) => downloadCurrentMasterContent({ retailerId, targetUrl }),
    lastUpdatedUrl: `https://${targetUrl}/api/account/GetLastUploadTime`,
    fileUploadUrl: `https://${targetUrl}/api/account/UserUpload?uploadType=${MASTER_CONTENT_UPLOAD_TYPE}`,
    currentMasterContentUrl: `https://${targetUrl}/api/account/DownloadLatestUploadedFile`,
    fetchLastUpdatedFunction: ({ retailerId }) => fetchLastUpdatedTime({ retailerId, targetUrl })
  };
};
